/**
 * Configuration Generators - Main Export
 * 
 * This module provides utilities for automatically generating CultureMap configurations
 * from Fulcrum forms, including field importance analysis and reverse relationship mapping.
 */

import { fieldImportanceGenerator } from './fieldImportanceGenerator';
import { reverseRelationshipGenerator } from './reverseRelationshipGenerator';

// Field Importance Generator
export {
  analyzeField,
  analyzeForm,
  analyzeForms,
  generateEndpointConfig,
  autoGenerateConfiguration,
  fieldImportanceGenerator
} from './fieldImportanceGenerator';

export type {
  FieldImportanceResult,
  FormAnalysisResult,
  ConfigGenerationOptions
} from './fieldImportanceGenerator';

// Reverse Relationship Generator
export {
  analyzeFormRelationships,
  analyzeAllFormRelationships,
  generateReverseRelationships,
  generateFormToRelationshipMapping,
  generateRelationshipConfiguration,
  generateAdminCompatibleConfig,
  validateRelationshipConfiguration,
  reverseRelationshipGenerator
} from './reverseRelationshipGenerator';

export type {
  RecordLinkField,
  FormRelationshipAnalysis,
  RelationshipMapping,
  ReverseRelationshipConfig,
  RelationshipGenerationOptions
} from './reverseRelationshipGenerator';

// ============================================================================
// UNIFIED CONFIGURATION GENERATOR
// ============================================================================

/**
 * Generate complete CultureMap configuration including field mappings and relationships
 */
export const generateCompleteConfiguration = async (
  userProfile: any,
  options: {
    includeHiddenFields?: boolean;
    prioritizeUserFriendlyLabels?: boolean;
    includeMetadata?: boolean;
    maxPopupFields?: number;
    includeAllForms?: boolean;
    autoDetectRelationshipKeys?: boolean;
  } = {}
): Promise<any> => {
  try {
    console.log('[ConfigGenerators] Generating complete configuration...');
    
    // Generate field importance analysis
    const fieldConfig = await fieldImportanceGenerator.autoGenerateConfiguration(userProfile, { 
      ...options, 
      userProfile 
    });
    
    // Generate relationship configuration
    const relationshipConfig = await reverseRelationshipGenerator.generateRelationshipConfiguration(userProfile, { 
      ...options, 
      userProfile 
    });
    
    // Merge configurations
    const completeConfig = {
      name: "Auto-generated Complete Configuration",
      description: "Generated by CultureMap Configuration Generators",
      version: "1.0.0",
      generatedAt: new Date().toISOString(),
      
      // API Endpoints with field mappings
      apiEndpoints: fieldConfig.apiEndpoints,
      
      // Relationship configurations
      formToRelationshipMapping: relationshipConfig.formToRelationshipMapping,
      reverseRelationships: relationshipConfig.reverseRelationships,
      
      // Analysis metadata
      _analysis: {
        fieldAnalysis: fieldConfig.analysisResults,
        relationshipAnalysis: relationshipConfig.relationshipAnalysis,
        statistics: {
          totalForms: fieldConfig.analysisResults.length,
          totalEndpoints: fieldConfig.apiEndpoints.length,
          totalRelationships: relationshipConfig.totalRelationships,
          formsWithMedia: fieldConfig.analysisResults.filter((a: any) => a.mediaFields.length > 0).length,
          formsWithDates: fieldConfig.analysisResults.filter((a: any) => a.dateFields.length > 0).length,
          formsWithRelationships: fieldConfig.analysisResults.filter((a: any) => a.relationshipFields.length > 0).length
        }
      }
    };
    
    console.log('[ConfigGenerators] Complete configuration generated successfully:', {
      endpoints: completeConfig.apiEndpoints.length,
      relationships: completeConfig._analysis.statistics.totalRelationships,
      formsWithMedia: completeConfig._analysis.statistics.formsWithMedia,
      formsWithDates: completeConfig._analysis.statistics.formsWithDates
    });
    
    return completeConfig;
    
  } catch (error) {
    console.error('[ConfigGenerators] Error generating complete configuration:', error);
    throw error;
  }
};

/**
 * Generate configuration JSON string ready for config.json
 */
export const generateConfigurationJSON = async (
  userProfile: any,
  options: Parameters<typeof generateCompleteConfiguration>[1] = {}
): Promise<string> => {
  const config = await generateCompleteConfiguration(userProfile, options);
  return JSON.stringify(config, null, 2);
};

/**
 * Validate generated configuration structure
 */
export const validateConfiguration = (config: any): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  try {
    // Check basic structure
    if (!config.apiEndpoints || !Array.isArray(config.apiEndpoints)) {
      errors.push('Missing or invalid apiEndpoints array');
    }
    
    if (!config.formToRelationshipMapping || typeof config.formToRelationshipMapping !== 'object') {
      errors.push('Missing or invalid formToRelationshipMapping');
    }
    
    // Validate endpoints
    if (config.apiEndpoints) {
      for (const [index, endpoint] of config.apiEndpoints.entries()) {
        if (!endpoint.name || !endpoint.formId) {
          errors.push(`Endpoint ${index}: Missing name or formId`);
        }
        
        if (!endpoint.dataSourceConfig?.fieldMapping) {
          errors.push(`Endpoint ${index}: Missing fieldMapping in dataSourceConfig`);
        }
      }
    }
    
    // Validate relationships using existing validator
    if (config.reverseRelationships) {
      const relationshipValid = reverseRelationshipGenerator.validateRelationshipConfiguration({
        formToRelationshipMapping: config.formToRelationshipMapping,
        reverseRelationships: config.reverseRelationships
      });
      
      if (!relationshipValid) {
        errors.push('Invalid relationship configuration structure');
      }
    }
    
    return { valid: errors.length === 0, errors };
    
  } catch (error) {
    errors.push(`Validation error: ${error instanceof Error ? error.message : String(error)}`);
    return { valid: false, errors };
  }
};

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

/**
 * Main configuration generator object with all utilities
 */
export const configGenerators = {
  field: fieldImportanceGenerator,
  relationship: reverseRelationshipGenerator,
  generateComplete: generateCompleteConfiguration,
  generateJSON: generateConfigurationJSON,
  validate: validateConfiguration
};

/**
 * Default export for easy importing
 */
export default configGenerators;